export const translations = {
  en: {
    nav: {
      manufacturing: 'Manufacturing',
      inspection: 'Inspection',
      fieldServices: 'Field Services',
      reach: 'Reach',
      gallery: 'Gallery',
    },
    hero: {
      rotations: [
        'Wind Turbine Blade Manufacturing, Inspection & Repair',
        'The First Mexican Company in Wind Blade Manufacturing Worldwide',
        'X-Ray Drone Inspection. On-Tower Repair. Zero Downtime.',
      ],
      subtitle: 'Based on the US-Mexico border. Deployed across the Americas.',
    },
    clients: {
      trustedBy: 'Trusted by:',
      certified: 'Certified:',
    },
    verticals: {
      card1: {
        title: 'Blade Manufacturing',
        copy: 'We produce wind turbine blades from our facility in Matamoros, Tamaulipas. Millions invested in equipment. 60 specialists hired. Warehouses on both sides of the border stocked with raw materials. We already produce for Nordex. Vestas operations in Colorado are next.',
      },
      card2: {
        title: 'Drone Inspection & NDT',
        copy: "Our inspection drones carry X-ray NDT sensors and AI that reads the internal condition of each blade while the turbine keeps running. Delamination, moisture ingress, subsurface fissures. All caught before they become costly failures.",
      },
      card3: {
        title: 'Field Services',
        copy: "IRATA and GWO certified technicians do composite patching and structural repairs directly on the tower. The turbine goes back online the same day. No crane mobilized. No production days lost.",
      },
    },
    manufacturing: {
      blocks: [
        {
          title: 'The only Mexican-owned wind blade manufacturer operating globally',
          copy: "When we started Yira, nobody in Mexico was manufacturing turbine blades for international OEMs. The Matamoros plant now runs with 60 trained technicians, dedicated production lines, and a materials inventory that includes epoxy resins, structural fibers, and consumables stored in Tamaulipas and South Texas. Every person on the manufacturing floor goes through an internal certification program before they touch a blade.",
        },
        {
          title: 'Producing for the names that matter',
          copy: "Nordex awarded Yira their Matamoros production work. TPI Composites partners with Yira across the United States and Mexico. Vestas has engaged the team for upcoming operations in Colorado. These are production contracts with companies that spend hundreds of millions annually on blade supply chains. Yira earned every one of them through execution. Not a single one through a sales pitch.",
        },
        {
          title: 'Texas to Peru. Same crew, any wind farm.',
          copy: "Yira sits on the US-Mexico border: Brownsville, Texas and Matamoros, Tamaulipas. From there, crews deploy across Latin America and the United States. Active projects in Mexico and Peru. The team handles the regulatory paperwork, work visa logistics, and trade compliance that cross-border wind projects require. They have operated in protected natural reserves and environmentally sensitive sites where permitting alone stops most contractors from even bidding.",
        },
      ],
    },
    inspection: {
      blocks: [
        {
          title: 'What cameras miss, X-ray finds',
          copy: "A standard blade inspection puts cameras on a drone and photographs the exterior. Visible cracks, surface erosion, coating damage. The problem is that the failures that actually cost money start inside the composite, where no camera can reach. Yira's drones carry X-ray NDT sensors paired with AI that reads the internal condition of each blade while the turbine keeps running. Delamination, moisture ingress, subsurface fissures. All caught before they turn into a $500,000 replacement.",
        },
        {
          title: 'Predictive maintenance, not reactive repairs',
          copy: "Most operators wait until a blade fails to schedule repairs. By then the damage has compounded and the costs have multiplied. Yira's NDT data feeds into predictive models that flag degradation trends months before failure. Operators get a maintenance roadmap, not an emergency call. This shifts the economics from reactive spending to planned budgets.",
        },
        {
          title: 'Drones in the air, robots on the ground',
          copy: "Yira runs two inspection systems. Drones equipped with optical, thermal, and X-ray sensors scan blades while the turbine runs. For dismounted blades, ground robots with high-resolution cameras and precision sensors crawl the surface detecting microfissures, erosion, and hidden damage without manual intervention. Both systems produce digital twins that clients use for warranty claims, insurance documentation, and maintenance planning.",
        },
      ],
    },
    fieldServices: {
      blocks: [
        {
          title: 'Repaired at height. Back online the same day.',
          copy: "The standard repair process: mobilize a crane, lower the blade, truck it to a workshop. Weeks of downtime. Revenue lost every day the turbine sits idle. Yira's technicians are IRATA and GWO rope-access certified. They do composite patching and structural repairs directly on the tower. In Colombia, the team deployed certified technicians to treat blades with severe structural and aerodynamic damage directly on-tower using certified materials and advanced restoration solutions. The turbine goes back online the same day.",
        },
        {
          title: 'Colombia, Mesa Dolores, Peru. Proven across the Americas.',
          copy: "At the Mesamorenos wind farm, Yira crews performed corrective maintenance on blades with erosion and surface delamination using rope-access equipment and certified materials. In Peru, the team executed ground-level repairs on dismounted blades with structural damage and environmental wear. Every project follows the same protocol: technical evaluation, certified materials, rigorous quality control. This is how Yira has never lost a single client to quality issues. Not one, in the entire history of the company.",
        },
      ],
    },
    numbers: {
      stats: [
        { value: '20+', label: 'Years of experience' },
        { value: '60+', label: 'Specialized personnel' },
        { value: '4+', label: 'Countries' },
        { value: '0', label: 'Clients lost to quality' },
      ],
      rotations: [
        "We don't just service turbines. We manufacture them.",
        'Full year of contracted work secured.',
        'Warehouses stocked on both sides of the border.',
        'Internal certification required before any technician touches a blade.',
      ],
    },
    reach: {
      heading: 'Powering the Americas',
      subtitle: 'Based on the US-Mexico border. Deployed across the continent.',
      locations: [
        { name: 'Matamoros, Tamaulipas', label: 'Manufacturing HQ & Operations Base', highlight: true },
        { name: 'Brownsville, Texas', label: 'US Operations & Warehousing', highlight: false },
        { name: 'Colorado, USA', label: 'Vestas Operations (Upcoming)', highlight: false },
        { name: 'Mexico (Nationwide)', label: 'Active Wind Farm Projects', highlight: false },
        { name: 'Colombia', label: 'On-Tower Blade Repair Projects', highlight: false },
        { name: 'Peru', label: 'Active Field Deployments', highlight: false },
      ],
    },
    gallery: {
      captions: [
        'Site deployment, South Texas wind corridor',
        'NDT inspection with real-time AI analysis',
        'On-tower composite repair, IRATA Level 3 crew',
        'Production line, Matamoros facility',
        'Field operations in a federally protected ecological reserve',
        'Structural repair at height, hand layup process',
      ],
    },
    footer: {
      location: 'Brownsville, TX, USA | Matamoros, Tamaulipas, Mexico',
      certifications: 'GWO | IRATA | ISO Certified',
      tagline: 'Blade Manufacturing. Inspection. Repair.',
    },
  },
  es: {
    nav: {
      manufacturing: 'Fabricación',
      inspection: 'Inspección',
      fieldServices: 'Servicios en Campo',
      reach: 'Alcance',
      gallery: 'Galería',
    },
    hero: {
      rotations: [
        'Fabricaci\u00F3n, Inspecci\u00F3n y Reparaci\u00F3n de Palas de Turbinas E\u00F3licas',
        'La Primera Empresa Mexicana en Fabricaci\u00F3n de Palas E\u00F3licas a Nivel Mundial',
        'Inspecci\u00F3n con Drones de Rayos X. Reparaci\u00F3n en Torre. Cero Tiempo Muerto.',
      ],
      subtitle: 'Con base en la frontera US-M\u00E9xico. Desplegados en todo el continente.',
    },
    clients: {
      trustedBy: 'Trabajan con nosotros:',
      certified: 'Certificados:',
    },
    verticals: {
      card1: {
        title: 'Fabricaci\u00F3n de Palas',
        copy: 'Producimos palas de turbinas e\u00F3licas desde nuestra planta en Matamoros, Tamaulipas. Millones invertidos en equipo. 60 especialistas contratados. Bodegas en ambos lados de la frontera llenas de materia prima. Ya producimos para Nordex. Vestas en Colorado es el siguiente paso.',
      },
      card2: {
        title: 'Inspección con Drones y NDT',
        copy: 'Nuestros drones llevan sensores NDT de rayos X con IA que lee la condición interna de cada pala mientras la turbina sigue operando. Delaminación, ingreso de humedad, fisuras subsuperficiales. Todo detectado antes de que se convierta en una falla costosa.',
      },
      card3: {
        title: 'Servicios en Campo',
        copy: 'Técnicos certificados IRATA y GWO hacen parcheo de compuestos y reparaciones estructurales directamente en la torre. La turbina regresa a operación el mismo día. Sin grúa. Sin días de producción perdidos.',
      },
    },
    manufacturing: {
      blocks: [
        {
          title: 'El \u00FAnico fabricante mexicano de palas e\u00F3licas operando a nivel global',
          copy: "Cuando fundamos Yira, nadie en M\u00E9xico fabricaba palas de turbinas para OEMs internacionales. La planta de Matamoros ahora opera con 60 t\u00E9cnicos capacitados, l\u00EDneas de producci\u00F3n dedicadas, y un inventario de materiales que incluye resinas ep\u00F3xicas, fibras estructurales y consumibles almacenados en Tamaulipas y el sur de Texas. Cada persona en el piso de manufactura pasa por un programa de certificaci\u00F3n interno antes de tocar una pala.",
        },
        {
          title: 'Produciendo para los nombres que importan',
          copy: "Nordex adjudic\u00F3 a Yira su trabajo de producci\u00F3n en Matamoros. TPI Composites trabaja con Yira en Estados Unidos y M\u00E9xico. Vestas ha contratado al equipo para operaciones pr\u00F3ximas en Colorado. Son contratos de producci\u00F3n con empresas que gastan cientos de millones al a\u00F1o en cadenas de suministro de palas. Yira se gan\u00F3 cada uno con ejecuci\u00F3n. Ninguno con un pitch de ventas.",
        },
        {
          title: 'De Texas a Per\u00FA. Mismo equipo, cualquier parque e\u00F3lico.',
          copy: "Yira est\u00E1 en la frontera US-M\u00E9xico: Brownsville, Texas y Matamoros, Tamaulipas. Desde ah\u00ED, los equipos se despliegan por Latinoam\u00E9rica y Estados Unidos. Proyectos activos en M\u00E9xico y Per\u00FA. El equipo maneja los tr\u00E1mites regulatorios, log\u00EDstica de visas de trabajo, y cumplimiento comercial que los proyectos e\u00F3licos transfronterizos requieren. Han operado en reservas naturales protegidas y sitios ambientalmente sensibles donde solo los permisos detienen a la mayor\u00EDa de los contratistas.",
        },
      ],
    },
    inspection: {
      blocks: [
        {
          title: 'Lo que las cámaras no ven, los rayos X lo encuentran',
          copy: "Una inspección estándar de palas pone cámaras en un dron y fotografía el exterior. Grietas visibles, erosión superficial, daño en recubrimiento. El problema es que las fallas que realmente cuestan dinero empiezan dentro del compuesto, donde ninguna cámara llega. Los drones de Yira llevan sensores NDT de rayos X con IA que lee la condición interna de cada pala mientras la turbina sigue operando. Delaminación, ingreso de humedad, fisuras subsuperficiales. Todo detectado antes de que se convierta en un reemplazo de $500,000.",
        },
        {
          title: 'Mantenimiento predictivo, no reparaciones reactivas',
          copy: "La mayoría de los operadores esperan a que una pala falle para programar reparaciones. Para entonces el daño se ha acumulado y los costos se han multiplicado. Los datos NDT de Yira alimentan modelos predictivos que detectan tendencias de degradación meses antes de la falla. Los operadores reciben una hoja de ruta de mantenimiento, no una llamada de emergencia.",
        },
        {
          title: 'Drones en el aire, robots en el piso',
          copy: "Yira opera dos sistemas de inspección. Drones equipados con sensores ópticos, térmicos y de rayos X escanean palas mientras la turbina opera. Para palas desmontadas, robots terrestres con cámaras de alta resolución y sensores de precisión recorren la superficie detectando microfisuras, erosión y daños ocultos sin intervención manual. Ambos sistemas producen gemelos digitales que los clientes usan para reclamos de garantía, documentación de seguros y planificación de mantenimiento.",
        },
      ],
    },
    fieldServices: {
      blocks: [
        {
          title: 'Reparado en altura. De vuelta en operación el mismo día.',
          copy: "El proceso estándar de reparación: movilizar una grúa, bajar la pala, transportarla a un taller. Semanas de tiempo muerto. Los técnicos de Yira están certificados en acceso con cuerda por IRATA y GWO. Hacen parcheo de compuestos y reparaciones estructurales directamente en la torre. En Colombia, el equipo desplegó técnicos certificados para tratar palas con daños severos estructurales y aerodinámicos directamente sobre torre con materiales certificados y soluciones de restauración avanzada. La turbina regresa a operación el mismo día.",
        },
        {
          title: 'Colombia, Mesa Dolores, Perú. Probados en toda América.',
          copy: "En el parque eólico Mesamorenos, los equipos de Yira realizaron mantenimiento correctivo en palas con erosión y delaminación superficial usando equipos de acceso por cuerda y materiales certificados. En Perú, el equipo ejecutó reparaciones en piso de palas desmontadas con daño estructural y desgaste ambiental. Cada proyecto sigue el mismo protocolo: evaluación técnica, materiales certificados, control de calidad riguroso. Así es como Yira no ha perdido nunca un solo cliente por temas de calidad. Ni uno, en toda la historia de la empresa.",
        },
      ],
    },
    numbers: {
      stats: [
        { value: '20+', label: 'A\u00F1os de experiencia' },
        { value: '60+', label: 'Personal especializado' },
        { value: '4+', label: 'Pa\u00EDses' },
        { value: '0', label: 'Clientes perdidos por calidad' },
      ],
      rotations: [
        'No solo damos servicio a turbinas. Las fabricamos.',
        'Año completo de trabajo contratado asegurado.',
        'Bodegas abastecidas en ambos lados de la frontera.',
        'Certificación interna requerida antes de que cualquier técnico toque una pala.',
      ],
    },
    reach: {
      heading: 'Presencia en las Américas',
      subtitle: 'Con base en la frontera US-México. Desplegados en todo el continente.',
      locations: [
        { name: 'Matamoros, Tamaulipas', label: 'HQ de Fabricación y Base de Operaciones', highlight: true },
        { name: 'Brownsville, Texas', label: 'Operaciones US y Almacenamiento', highlight: false },
        { name: 'Colorado, USA', label: 'Operaciones Vestas (Próximamente)', highlight: false },
        { name: 'México (Nacional)', label: 'Proyectos Activos en Parques Eólicos', highlight: false },
        { name: 'Colombia', label: 'Proyectos de Reparación en Torre', highlight: false },
        { name: 'Perú', label: 'Despliegues Activos en Campo', highlight: false },
      ],
    },
    gallery: {
      captions: [
        'Despliegue en sitio, corredor e\u00F3lico del sur de Texas',
        'Inspecci\u00F3n NDT con an\u00E1lisis de IA en tiempo real',
        'Reparaci\u00F3n de compuestos en torre, equipo IRATA Nivel 3',
        'L\u00EDnea de producci\u00F3n, planta de Matamoros',
        'Operaciones en campo en reserva ecol\u00F3gica protegida federal',
        'Reparaci\u00F3n estructural en altura, proceso de laminado manual',
      ],
    },
    footer: {
      location: 'Brownsville, TX, USA | Matamoros, Tamaulipas, M\u00E9xico',
      certifications: 'Certificados GWO | IRATA | ISO',
      tagline: 'Fabricaci\u00F3n de Palas. Inspecci\u00F3n. Reparaci\u00F3n.',
    },
  },
};

export const images = {
  heroImages: [
    'https://static.prod-images.emergentagent.com/jobs/dd307d75-d668-4671-ade5-dd0b41583331/images/d98e4663deda27343a37fe46e76149f30af254053b13e5d3d37611416250f358.png',
    'https://static.prod-images.emergentagent.com/jobs/dd307d75-d668-4671-ade5-dd0b41583331/images/e6f396f92bc0f46a017c17ee0233c22158a255fb05ce2b8909a37d263be6c154.png',
    'https://static.prod-images.emergentagent.com/jobs/dd307d75-d668-4671-ade5-dd0b41583331/images/9c1340442d2aa9bbb5c4375765958a4ac77a2e23f313c62d38fc3766b3173bae.png',
  ],
  card1: 'https://static.prod-images.emergentagent.com/jobs/dd307d75-d668-4671-ade5-dd0b41583331/images/153ad9e644680de89a503eb84973f7b1fe7035fbdbec88f3fc93bacbdfa8140c.png',
  card2: 'https://images.unsplash.com/photo-1758665156000-d05091c8c612?auto=format&fit=crop&w=800&q=85',
  mfg1: '/images/blade-manufacturing.png',
  mfg2: 'https://images.unsplash.com/photo-1772850322630-b4f1610d370f?auto=format&fit=crop&w=1000&q=85',
  mfg3: 'https://images.unsplash.com/photo-1680281109728-e2e40001a820?auto=format&fit=crop&w=1000&q=85',
  fs1: 'https://images.unsplash.com/photo-1773750923599-2434b82fdcd3?auto=format&fit=crop&w=1000&q=85',
  fs2: 'https://images.unsplash.com/photo-1623068565971-15284f000726?auto=format&fit=crop&w=1000&q=85',
  fs3: '/images/tab-fieldservice.png',
  card3: '/images/bento-repair-bright.png',
  insp1: '/images/drone-inspection.png',
  insp2: '/images/gallery-xray-inspection.png',
  insp3: 'https://yirawindservices.mx/wp-content/uploads/2025/11/inspeccion-de-aspas-eolicas-con-drone.webp',
  gallery: [
    'https://images.unsplash.com/photo-1772850324031-789e15b44d84?auto=format&fit=crop&w=1200&q=85',
    'https://images.unsplash.com/photo-1766405530188-69c8fe8ed970?auto=format&fit=crop&w=1200&q=85',
    'https://images.unsplash.com/photo-1764925925904-66da3bffecba?auto=format&fit=crop&w=1200&q=85',
    'https://static.prod-images.emergentagent.com/jobs/dd307d75-d668-4671-ade5-dd0b41583331/images/153ad9e644680de89a503eb84973f7b1fe7035fbdbec88f3fc93bacbdfa8140c.png',
    'https://images.unsplash.com/photo-1667231949428-bd558922f39a?auto=format&fit=crop&w=1200&q=85',
    'https://images.unsplash.com/photo-1763478530231-7c6014c577e0?auto=format&fit=crop&w=1200&q=85',
  ],
};
